/**
 * Message encoding/decoding for all protocol messages
 *
 * This file contains encode/decode functions for messages that aren't
 * part of the core game state (movement, snapshot, build).
 *
 * Client → Server:
 * - MSG_JOIN: Player joining a room
 * - MSG_PING: Latency measurement
 *
 * Server → Client:
 * - MSG_WELCOME: Room assignment confirmation
 * - MSG_ROOM_INFO: Player count update
 * - MSG_PONG: Ping response
 * - MSG_ERROR: Error notification
 */
import { ByteWriter } from '../util/bytes.js';
import { MSG_JOIN, MSG_PING, MSG_WELCOME, MSG_ROOM_INFO, MSG_PONG, MSG_ERROR, MSG_REQUEST_NACK, } from './msgIds.js';
// ============== Client → Server ==============
/**
 * Encode JOIN message
 * Sent after HTTP join to establish WebSocket connection
 */
export function encodeJoin(protocolVersion, playerId) {
    const writer = new ByteWriter(8);
    writer.writeUint8(MSG_JOIN);
    writer.writeUint8(protocolVersion);
    writer.writeUint16(playerId);
    return writer.toUint8Array();
}
/**
 * Decode JOIN message
 */
export function decodeJoin(reader) {
    return {
        protocolVersion: reader.readUint8(),
        playerId: reader.readUint16(),
    };
}
/**
 * Encode PING message with timestamp
 */
export function encodePing(timestamp) {
    const writer = new ByteWriter(8);
    writer.writeUint8(MSG_PING);
    writer.writeUint32(timestamp);
    return writer.toUint8Array();
}
/**
 * Decode PING message
 */
export function decodePing(reader) {
    return {
        timestamp: reader.readUint32(),
    };
}
// ============== Server → Client ==============
/**
 * Encode WELCOME message
 * Confirms room assignment to client. Carries the server's world seed (trailing uint32) so the
 * client can generate far-view LOD terrain locally with matching noise — appended AFTER the
 * original fields, so old decoders that stop at the room id are unaffected (backwards-safe).
 *
 * ...and then the server's GENERATION CONFIG, appended the same way and for the same reason. Until
 * this existed the server was the only generator in the system that could not be configured: every
 * server-side TerrainGenerator was `new TerrainGenerator({ seed })` and nothing else, so a tuned
 * world was a single-player feature and the client's far-view rings were generated from a config the
 * server had never heard of. Length-prefixed JSON, because it is sent once per join and the shape is
 * the node tree's — a binary encoding of it would have to be re-derived every time a kind is added,
 * which is the thing the catalog exists to stop.
 */
export function encodeWelcome(playerId, roomId, worldSeed = 0, genConfigJson = '') {
    const roomBytes = new TextEncoder().encode(roomId);
    const cfgBytes = new TextEncoder().encode(genConfigJson);
    const writer = new ByteWriter(12 + roomBytes.length + cfgBytes.length);
    writer.writeUint8(MSG_WELCOME);
    writer.writeUint16(playerId);
    // Write room ID as length-prefixed string
    writer.writeUint8(roomBytes.length);
    for (let i = 0; i < roomBytes.length; i++) {
        writer.writeUint8(roomBytes[i]);
    }
    writer.writeUint32(worldSeed >>> 0);
    writer.writeUint32(cfgBytes.length);
    for (let i = 0; i < cfgBytes.length; i++)
        writer.writeUint8(cfgBytes[i]);
    return writer.toUint8Array();
}
/**
 * Decode WELCOME message. `worldSeed` and `genConfigJson` are undefined when talking to an older
 * server that doesn't send them — far-view LOD stays off without the seed, and the engine defaults
 * stand in without the config.
 */
export function decodeWelcome(reader) {
    const playerId = reader.readUint16();
    const roomLength = reader.readUint8();
    const roomBytes = [];
    for (let i = 0; i < roomLength; i++) {
        roomBytes.push(reader.readUint8());
    }
    const roomId = String.fromCharCode(...roomBytes);
    const worldSeed = reader.remaining >= 4 ? reader.readUint32() : undefined;
    let genConfigJson;
    if (reader.remaining >= 4) {
        const len = reader.readUint32();
        if (len > 0 && reader.remaining >= len) {
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++)
                bytes[i] = reader.readUint8();
            genConfigJson = new TextDecoder().decode(bytes);
        }
    }
    return { playerId, roomId, worldSeed, genConfigJson };
}
/**
 * Encode ROOM_INFO message
 */
export function encodeRoomInfo(playerCount) {
    const writer = new ByteWriter(4);
    writer.writeUint8(MSG_ROOM_INFO);
    writer.writeUint8(playerCount);
    return writer.toUint8Array();
}
/**
 * Decode ROOM_INFO message
 */
export function decodeRoomInfo(reader) {
    return {
        playerCount: reader.readUint8(),
    };
}
/**
 * Encode PONG message (echoes client timestamp)
 */
export function encodePong(timestamp) {
    const writer = new ByteWriter(8);
    writer.writeUint8(MSG_PONG);
    writer.writeUint32(timestamp);
    return writer.toUint8Array();
}
/**
 * Decode PONG message
 */
export function decodePong(reader) {
    return {
        timestamp: reader.readUint32(),
    };
}
/**
 * Encode ERROR message
 */
export function encodeError(errorCode) {
    const writer = new ByteWriter(4);
    writer.writeUint8(MSG_ERROR);
    writer.writeUint8(errorCode);
    return writer.toUint8Array();
}
/**
 * Decode ERROR message
 */
export function decodeError(reader) {
    return {
        errorCode: reader.readUint8(),
    };
}
/**
 * Encode a REQUEST_NACK — server tells client a request was dropped.
 */
export function encodeRequestNack(originalMsgId, x, y, z) {
    const writer = new ByteWriter(8);
    writer.writeUint8(MSG_REQUEST_NACK);
    writer.writeUint8(originalMsgId);
    writer.writeInt16(x);
    writer.writeInt16(y);
    writer.writeInt16(z);
    return writer.toUint8Array();
}
/**
 * Decode a REQUEST_NACK.
 */
export function decodeRequestNack(reader) {
    return {
        originalMsgId: reader.readUint8(),
        x: reader.readInt16(),
        y: reader.readInt16(),
        z: reader.readInt16(),
    };
}
//# sourceMappingURL=messages.js.map